/**
 * Which surface a view renders as, in the VIEWER's terms. The pure decision
 * (`viewRepresentation`) lives in viewer-core, where the headless surfaces and
 * the nav query read it too; what stays here is the live-surface layer on top —
 * which representation the board should render RIGHT NOW, and what chrome that
 * implies.
 */
import { resolveViewInstance, type ViewDeclV2 } from "@archlang/engine";
import { viewRepresentation, type ViewRepresentation } from "@archlang/viewer-core";

export { viewRepresentation };
export type { ViewRepresentation };

/**
 * The representation the board surface should render RIGHT NOW: an in-browser
 * `draftView` (the live builder preview) wins over the named/tab view — same
 * precedence the projection uses (`computeViewSurfaceResult` renders draftView
 * first). Without a draft, the effective view name decides (qualified first).
 *
 * Load-bearing: the projection and this switch MUST agree, or a draft with a
 * `table`/`matrix`/`flow` clause projects onto the board while the switch still
 * says "board" (the "table selected but I still see a board" bug).
 */
export function activeViewRepresentation(
  draftView: ViewDeclV2 | null,
  effectiveViewName: string | null,
  views: readonly ViewDeclV2[] | undefined,
): ViewRepresentation {
  const byName = viewsByNameOf(views);
  if (draftView) return viewRepresentation(draftView, byName);
  return viewRepresentation(findView(views, effectiveViewName), byName);
}

/**
 * Whether the board's playback step-scrubber HUD should show. The scrubber is a
 * BOARD HUD — it drives step highlighting on board NODES — so it only makes sense
 * over the board representation. On a tabular/index/matrix/grid/flow surface there
 * are no board nodes to scrub, so an active process must NOT leak its scrubber as
 * a floating overlay over that surface (the "scrubber over the index" bug). Flow
 * carries its own in-lane scrubber inside ProcessMode; this is only the top-level
 * board HUD.
 */
export function boardScrubberVisible(repr: ViewRepresentation, hasActiveProcess: boolean): boolean {
  return repr === "board" && hasActiveProcess;
}

/** The name→decl map {@link viewRepresentation} resolves instance parents
 *  against (first declaration wins on a shadowed bare name — deterministic). */
export function viewsByNameOf(
  views: readonly ViewDeclV2[] | undefined,
): ReadonlyMap<string, ViewDeclV2> {
  const m = new Map<string, ViewDeclV2>();
  for (const v of views ?? []) if (!m.has(v.name)) m.set(v.name, v);
  return m;
}

/** A view's fully-qualified name — owner path + name, prefixed with its
 *  landed space (`Space::Owner.Name`, v0.11 D2) when it has one. The id the
 *  projection, tabs, and share links address it by; a cross-space twin needs
 *  the space term to stay uniquely addressable. Bare for spaceless views. */
export function qualifiedViewName(view: ViewDeclV2): string {
  const qn = [...(view.ownerPath ?? []), view.name].join(".");
  return view.space && view.space.length > 0 ? `${view.space.join(".")}::${qn}` : qn;
}

/** The display form of a view addressing id — the `Space::` term stripped
 *  (display stays bare; the space reads as a secondary chip, v0.11 D5). */
export function bareViewLabel(nameOrId: string): string {
  const i = nameOrId.lastIndexOf("::");
  return i >= 0 ? nameOrId.slice(i + 2) : nameOrId;
}

/**
 * Resolve a view by name: a qualified match first (owner-scoped views), then a
 * bare-name fallback — the ONE lookup precedence every surface shares (was
 * inlined in the projection, the surface hook, and the representation switch).
 * Host-agnostic view rendering (a `ViewRenderer` outside the workbench) resolves
 * its target through here.
 */
export function findView(
  views: readonly ViewDeclV2[] | undefined,
  name: string | null | undefined,
): ViewDeclV2 | undefined {
  if (!name || !views) return undefined;
  return views.find((v) => qualifiedViewName(v) === name) ?? views.find((v) => v.name === name);
}

/**
 * Resolve a view INSTANCE's parent decl exactly as the projection does: the
 * engine's `resolveViewInstance` consumes {@link viewsByNameOf} (bare name,
 * first declaration wins), so every UI reading the parent's decls (knobs,
 * table clause) MUST resolve through the same map. A qualified-first
 * `findView` here picked a DIFFERENT owner-scoped twin than the merge the
 * board actually renders (r36). Returns undefined for a non-instance.
 */
export function instanceParentOf(
  view: ViewDeclV2 | undefined | null,
  views: readonly ViewDeclV2[] | undefined,
): ViewDeclV2 | undefined {
  return view?.instanceOf ? viewsByNameOf(views).get(view.instanceOf) : undefined;
}

/** Every view that renders as `repr`, resolving instance parents (an instance of
 *  a table-carrying parent counts as `table`). Powers a catalogue/index of the
 *  views a workspace exposes at a given representation. */
export function viewsByRepresentation(
  views: readonly ViewDeclV2[] | undefined,
  repr: ViewRepresentation,
): readonly ViewDeclV2[] {
  const byName = viewsByNameOf(views);
  return (views ?? []).filter((v) => viewRepresentation(v, byName) === repr);
}

/** A view's authored description (markdown-flavor prose), or null. */
export function getViewDescription(view: ViewDeclV2 | undefined | null): string | null {
  return view?.description ?? null;
}
